import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApplicationRef, CUSTOM_ELEMENTS_SCHEMA, DoBootstrap, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { AuthGuard } from '@auth0/auth0-angular';
import { CoreModule } from '@core/core.module';
import { Auth0Provider } from '@core/providers/auth0-auth.provider';
import { HubAuthService } from '@core/services/hub-auth/hub-auth.service';
import { AppState } from '@core/store/application/application.state';
import { EntityNamesState } from '@core/store/entity-names/entity-names.state';
import { NavbarState } from '@core/store/navbar/navbar.state';
import { UserState } from '@core/store/user/user.state';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxsModule } from '@ngxs/store';
import { HubLoadingSpinnerModule } from '@shared/components/hub-loading-spinner/hub-loading-spinner.module';
import { provideMonacoEditorConfig } from '@core/utils/monaco-embed';
import { provideHubAccount } from '@core/services/ol-account/ol-account.providers';
import { AppRoutingModule } from '../app-routing.module';
import { DesignerEmbedRoot } from './designer-embed-root.component';
import { EmbedHttpAuthInterceptor } from './embed-http-auth.interceptor';
import { PlatformAuthGuard } from './platform-auth.guard';
import { PlatformAuthProvider } from './platform-auth.provider';

// Embed variant of AppModule. Imports the same feature/core machinery the
// standalone build uses but swaps the auth boundary (no @auth0/auth0-angular,
// no own HTTP token interceptor) for the platform-provided one. Has empty
// `bootstrap: []` because Angular Elements does the bootstrapping.
@NgModule({
  declarations: [DesignerEmbedRoot],
  imports: [
    BrowserModule,
    NoopAnimationsModule,
    CoreModule,
    NgxsModule.forRoot([AppState, UserState, NavbarState, EntityNamesState], {
      developmentMode: isDevMode(),
      compatibility: { strictContentSecurityPolicy: true },
    }),
    NgSelectModule,
    HubLoadingSpinnerModule,
    AppRoutingModule,
  ],
  providers: [
    // Designer is embedded in hub mode. <ol-header> renders @ol/kit's built-in
    // <ol-user-dropdown>, which constructs AccountStore — without these account
    // providers it throws NG0201 (no OL_ACCOUNT_CONFIG) and the header end fails
    // to render. Mirrors AppModule so the embedded header matches standalone.
    provideHubAccount('hub', '/account'),
    // Root-level so kit-hosted account sections (which resolve against the
    // root injector) can create <ngx-monaco-editor> — see monaco-embed.ts.
    // `bundled`: the host page CSP blocks the AMD loader <script> tag from the
    // remote origin, so the embed imports the ESM monaco through the
    // federation chunk channel instead (SH-15248 / SH-15251).
    provideMonacoEditorConfig({ bundled: true }),
    { provide: APP_BASE_HREF, useValue: '/ol-integration' },
    {
      provide: HubAuthService,
      useFactory: () =>
        new HubAuthService(null, new PlatformAuthProvider() as unknown as Auth0Provider, null),
    },
    { provide: AuthGuard, useExisting: PlatformAuthGuard },
    { provide: HTTP_INTERCEPTORS, useClass: EmbedHttpAuthInterceptor, multi: true },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppEmbedModule implements DoBootstrap {
  // No automatic bootstrap — DesignerEmbedRoot is mounted via Angular Elements
  // (createCustomElement) in main.ts. Defining ngDoBootstrap as a no-op tells
  // Angular this is intentional and avoids the NG0403 "no bootstrap" error.
  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method -- required no-op: DesignerEmbedRoot is mounted via Angular Elements, so an explicit (empty) ngDoBootstrap is needed to avoid NG0403 with `bootstrap: []`
  ngDoBootstrap(_appRef: ApplicationRef): void {
    // intentional no-op
  }
}
